import { CSSProperties, FC, useMemo } from "react";
import { useIntl } from "react-intl";
import { DefaultButton, DialogFooter, IDialogFooterStyles, PrimaryButton } from "@fluentui/react";
import { ErrorRecord, logger, themeUtils, useTheme } from "cayo.ui";
import { IFormFooter, FormThemes } from "@cayo/shared/api/types/schema";
import { getFormThemeColors } from "@cayo/shared/components/Form/renderers/renderers.buttons";
import { commonMessages } from "@cayo/shared/messages/common";
import { TabNavigationProps } from "@cayo/shared/components/Form/types";

type FormFooterProps = {
  footer?: IFormFooter | undefined;
  isOKDisabled?: boolean;
  errors: { [id: string]: ErrorRecord };
  saveError?: string;
  onSubmit?: () => Promise<void> | undefined;
  onClose?: () => void;
  style?: CSSProperties;
  tabNavigation?: TabNavigationProps;
  formTheme?: FormThemes;
};

const FormFooter: FC<FormFooterProps> = ({
  isOKDisabled,
  errors,
  saveError,
  onSubmit,
  onClose,
  footer,
  style,
  tabNavigation,
  formTheme,
}) => {
  const intl = useIntl();
  const { cayoTheme } = useTheme();

  const footerStyle = useMemo(() => {
    const result: Partial<IDialogFooterStyles> = {
      actions: {
        borderTop: tabNavigation ? `1px solid ${cayoTheme.brandColors.divider}` : undefined,
        flexWrap: "nowrap",
        padding: 0,
        ...((style as any) || {}),
        ".ms-Button": { minWidth: 90 },
      },
    };

    return result;
  }, [style, tabNavigation]);

  return (
    <DialogFooter styles={footerStyle}>
      {tabNavigation && (
        <DefaultButton
          onClick={tabNavigation.onPrev}
          text={intl.formatMessage(commonMessages.back)}
          disabled={!tabNavigation.onPrev}
        />
      )}
      {tabNavigation?.onNext && (
        <PrimaryButton
          onClick={async () => {
            try {
              await tabNavigation!.onNext!();
            } catch (e) {
              logger.error("Error in tab navigation onNext", e);
            }
          }}
          text={intl.formatMessage(commonMessages.next)}
        />
      )}
      {!tabNavigation?.onNext && onSubmit && footer?.okText && (
        <PrimaryButton
          onClick={onSubmit}
          iconProps={footer?.okIcon ? themeUtils.getIconProps(footer.okIcon) : undefined}
          styles={getFormThemeColors(formTheme, cayoTheme)}
          text={saveError ? intl.formatMessage(commonMessages.retry) : footer.okText}
          disabled={Object.keys(errors).length > 0 || isOKDisabled}
        />
      )}
      {onClose && (
        <DefaultButton
          onClick={onClose}
          text={footer?.cancelText || intl.formatMessage(commonMessages.no)}
        />
      )}
    </DialogFooter>
  );
};

export default FormFooter;
